import React, { useMemo, useState } from 'react';
import { useTheme } from '../ThemeContext';
import { agentFetch } from '../utils/agentApi';
import {
  CSV_MAX_BYTES,
  CSV_MAX_ROWS,
  IMPORT_CHUNK_SIZE,
  IMPORT_FIELDS,
  applyMapping,
  autoMatchHeaders,
  buildTemplateCsv,
  chunkRows,
  mappingMissingRequired,
  parseHomeownerCsv,
  rowsToCsv,
  CLIENT_TEMPLATE_FILENAME,
} from '../shared/csvIntake';
import { CSV_ATTESTATION } from '../shared/attestation';
import { isNativeApp, NATIVE_PLAN_NOTICE } from '../utils/platform';
import { shareTextFile, shareWasCancelled } from '../utils/nativeShare';

const ATTESTATION = CSV_ATTESTATION;

async function downloadText(filename, text) {
  try {
    await shareTextFile(filename, text, 'text/csv');
  } catch (error) {
    if (shareWasCancelled(error)) return;
    throw error;
  }
}

const HomeownerImport = ({ profile, onClose, onFinished }) => {
  const { colors } = useTheme();
  const [step, setStep] = useState('upload');
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [onDuplicate, setOnDuplicate] = useState('skip');
  const [sendInvites, setSendInvites] = useState(false);
  const [attested, setAttested] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState([]);
  const [batchId, setBatchId] = useState(null);

  const missing = useMemo(() => mappingMissingRequired(mapping), [mapping]);

  const readFile = async (file) => {
    setError('');
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Save the spreadsheet as CSV. Excel .xlsx files are not supported.');
      return;
    }
    if (file.size > CSV_MAX_BYTES) {
      setError('CSV files must be 2 MB or smaller.');
      return;
    }
    const text = await file.text();
    const parsed = parseHomeownerCsv(text);
    if (!parsed.ok) {
      setError(parsed.message);
      return;
    }
    if (parsed.rows.length === 0) {
      setError('That file has no data rows.');
      return;
    }
    setFileName(file.name);
    setHeaders(parsed.headers);
    setRawRows(parsed.rows);
    setMapping(autoMatchHeaders(parsed.headers));
    setStep('map');
  };

  const mappedRows = () => rawRows.map((row) => ({
    row: row.rowNumber,
    ...applyMapping(row.values, mapping),
  }));

  const runPreview = async () => {
    setBusy(true);
    setError('');
    try {
      const { ok, body } = await agentFetch('/api/homeowners/import', {
        method: 'POST',
        body: JSON.stringify({ mode: 'preview', rows: mappedRows() }),
      });
      if (!ok) {
        setError(body.error || 'Could not preview this file.');
        return;
      }
      setPreview(body);
      setStep('preview');
    } catch (err) {
      setError(err.message || 'Could not preview this file.');
    } finally {
      setBusy(false);
    }
  };

  const importableRows = () => {
    const byRow = new Map((preview?.rows || []).map((row) => [row.row, row]));
    return mappedRows().filter((row) => {
      const status = byRow.get(row.row)?.status;
      if (status === 'error' || status === 'limit_reached') return false;
      if (status === 'existing' && onDuplicate === 'skip') return false;
      return status === 'new' || status === 'existing';
    });
  };

  const runImport = async () => {
    if (!attested) {
      setError(ATTESTATION);
      return;
    }
    setBusy(true);
    setError('');
    setStep('import');
    const rows = importableRows();
    const chunks = chunkRows(rows, IMPORT_CHUNK_SIZE);
    setProgress({ done: 0, total: Math.max(chunks.length, 1) });
    try {
      const started = await agentFetch('/api/homeowners/import', {
        method: 'POST',
        body: JSON.stringify({
          mode: 'start',
          file_name: fileName,
          total_rows: rawRows.length,
          send_invites: sendInvites,
          on_duplicate: onDuplicate,
          attested_relationship: attested,
        }),
      });
      if (!started.ok) {
        setError(started.body.error || 'Could not start the import.');
        setStep('preview');
        return;
      }
      const id = started.body.batch_id;
      setBatchId(id);
      const collected = [];
      for (let index = 0; index < chunks.length; index += 1) {
        const committed = await agentFetch('/api/homeowners/import', {
          method: 'POST',
          body: JSON.stringify({
            mode: 'commit',
            batch_id: id,
            chunk_index: index,
            rows: chunks[index],
          }),
        });
        if (!committed.ok) {
          setError(committed.body.error || `Chunk ${index + 1} failed.`);
          break;
        }
        collected.push(...(committed.body.rows || []));
        setProgress({ done: index + 1, total: chunks.length });
      }
      await agentFetch('/api/homeowners/import', {
        method: 'POST',
        body: JSON.stringify({ mode: 'finish', batch_id: id }),
      });
      setResults(collected);
      setStep('done');
    } catch (err) {
      setError(err.message || 'Import failed.');
      setStep('preview');
    } finally {
      setBusy(false);
    }
  };

  const newCount = preview?.totals?.new || 0;
  const panel = {
    background: colors.surface,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '0.75rem',
    padding: '1.25rem',
    width: 'min(920px, 100%)',
    maxHeight: '90vh',
    overflow: 'auto',
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
      onClick={onClose}
    >
      <div style={panel} onClick={(event) => event.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>Import homeowners{profile?.first_name ? ` for ${profile.first_name}` : ''}</h2>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: 0, color: colors.textSecondary, fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
        </div>
        {error && <p style={{ color: '#b91c1c' }}>{error}</p>}

        {step === 'upload' && (
          <div>
            <p>Upload a .csv file. Excel workbooks need to be saved as CSV. Up to {CSV_MAX_ROWS} homeowners and 2 MB.</p>
            <a
              href={`/${CLIENT_TEMPLATE_FILENAME}`}
              download={CLIENT_TEMPLATE_FILENAME}
              type="text/csv"
              style={{
                display: 'inline-block',
                marginTop: '0.35rem',
                padding: '0.7rem 1rem',
                borderRadius: '999px',
                border: `1px solid ${colors.border}`,
                background: colors.surface,
                color: colors.text,
                fontWeight: 650,
                fontSize: '16px',
                textDecoration: 'none',
              }}
              onClick={(event) => {
                if (!isNativeApp()) return;
                event.preventDefault();
                downloadText(CLIENT_TEMPLATE_FILENAME, buildTemplateCsv()).catch((downloadError) => setError(downloadError.message || 'Could not share that file.'));
              }}
            >
              Download template
            </a>
            <div
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                readFile(event.dataTransfer.files?.[0]);
              }}
              style={{ marginTop: '1rem', padding: '2rem', border: `2px dashed ${colors.border}`, borderRadius: '0.75rem', textAlign: 'center' }}
            >
              Drop a CSV here
              <div style={{ marginTop: '0.75rem' }}>
                <input type="file" accept=".csv,text/csv" onChange={(event) => readFile(event.target.files?.[0])} />
              </div>
            </div>
          </div>
        )}

        {step === 'map' && (
          <div>
            <p>Match your columns. Required fields that are not mapped block the next step. A Full Name column can be split into first and last name.</p>
            {IMPORT_FIELDS.map((field) => (
              <label key={field.key} style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '0.75rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                <span>{field.label}{field.required ? ' *' : ''}</span>
                <select
                  value={mapping[field.key] || ''}
                  onChange={(event) => setMapping({ ...mapping, [field.key]: event.target.value || null })}
                >
                  <option value="">Not mapped</option>
                  {headers.map((header) => <option key={header} value={header}>{header}</option>)}
                </select>
              </label>
            ))}
            {missing.length > 0 && <p style={{ color: '#b91c1c' }}>Map {missing.join(', ')} before continuing.</p>}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button type="button" onClick={() => setStep('upload')}>Back</button>
              <button type="button" disabled={missing.length > 0 || busy} onClick={runPreview}>{busy ? 'Checking...' : 'Preview'}</button>
            </div>
          </div>
        )}

        {step === 'preview' && preview && (
          <div>
            <p>
              {preview.totals.new} new · {preview.totals.existing} already exist · {preview.totals.errors} errors · {preview.totals.limit_reached} over limit
            </p>
            <p>You can add {preview.capacity.remaining} more homeowners before new adds pause ({preview.capacity.used} / {preview.capacity.limit}). Existing homeowners keep access.</p>
            <div style={{ overflowX: 'auto', maxHeight: '280px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th>Row</th><th>Name</th><th>Email</th><th>Status</th><th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row) => (
                    <tr key={row.row}>
                      <td>{row.row}</td>
                      <td>{row.first_name} {row.last_name}</td>
                      <td>{row.email}</td>
                      <td>{row.status === 'limit_reached' ? 'Over plan limit' : row.status === 'existing' ? 'Already in your list' : row.status === 'new' ? 'New' : 'Error'}</td>
                      <td>{row.message || (row.warnings || []).join('; ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              onClick={() => {
                const report = preview.rows.map((row) => ({
                  row: row.row,
                  status: row.status,
                  message: row.message || (row.warnings || []).join('; '),
                  email: row.email,
                  first_name: row.first_name,
                  last_name: row.last_name,
                }));
                downloadText('homeowner-import-errors.csv', rowsToCsv(report)).catch((downloadError) => setError(downloadError.message || 'Could not share that file.'));
              }}
            >
              Download error report
            </button>
            <div style={{ marginTop: '1rem' }}>
              <label>
                <input type="radio" checked={onDuplicate === 'skip'} onChange={() => setOnDuplicate('skip')} /> Skip people already on your list
              </label>
              <label style={{ marginLeft: '1rem' }}>
                <input type="radio" checked={onDuplicate === 'update_blank'} onChange={() => setOnDuplicate('update_blank')} /> Fill blank fields on existing homeowners
              </label>
            </div>
            <label style={{ display: 'block', marginTop: '0.75rem' }}>
              <input type="checkbox" checked={attested} onChange={(event) => setAttested(event.target.checked)} /> {ATTESTATION}
            </label>
            <label style={{ display: 'block', marginTop: '0.75rem' }}>
              <input type="checkbox" checked={sendInvites} onChange={(event) => setSendInvites(event.target.checked)} />
              {' '}Email invites to the {newCount} new homeowners
            </label>
            {sendInvites && (
              <p>Today you can send {preview.invites.remaining_today} more invites; the rest will wait.</p>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button type="button" onClick={() => setStep('map')}>Back</button>
              <button type="button" disabled={busy || !attested || newCount + (onDuplicate === 'update_blank' ? preview.totals.existing : 0) === 0} onClick={runImport}>
                Import
              </button>
            </div>
            {preview.totals.limit_reached > 0 && (
              isNativeApp()
                ? <p>Some rows are over your plan limit. {NATIVE_PLAN_NOTICE}</p>
                : <p>Some rows are over your plan limit. <a href="/?login=agent&section=settings#billing">Upgrade</a> to add them.</p>
            )}
          </div>
        )}

        {step === 'import' && (
          <div>
            <p>Importing… {progress.done} / {progress.total} chunks</p>
            <div style={{ height: '8px', background: colors.border, borderRadius: '4px' }}>
              <div style={{ height: '8px', width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%`, background: '#59c4c4' }} />
            </div>
          </div>
        )}

        {step === 'done' && (
          <div>
            <p>
              Created {results.filter((row) => row.outcome === 'created').length}
              {' · '}Updated {results.filter((row) => row.outcome === 'updated').length}
              {' · '}Skipped {results.filter((row) => row.outcome === 'existing').length}
              {' · '}Errors {results.filter((row) => row.outcome === 'invalid' || row.outcome === 'internal').length}
              {' · '}Over limit {results.filter((row) => row.outcome === 'limit_reached').length}
            </p>
            <button
              type="button"
              onClick={() => downloadText('homeowner-import-results.csv', rowsToCsv(results.map((row) => ({
                email: row.email,
                name: `${row.first_name || ''} ${row.last_name || ''}`.trim(),
                invite_code: row.invite_code || '',
                outcome: row.outcome,
                invite_status: row.invite_status || '',
              })))).catch((downloadError) => setError(downloadError.message || 'Could not share that file.'))}
            >
              Download results CSV
            </button>
            <div style={{ marginTop: '1rem' }}>
              <button type="button" onClick={() => onFinished(batchId)}>View these homeowners</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeownerImport;
